export const AIRPORT_TYPES = [
  { types: ["large_airport"], label: "Large Airports" },
  {
    types: ["large_airport", "medium_airport"],
    label: "Large & Medium Airports",
  },
  {
    types: ["large_airport", "medium_airport", "small_airport"],
    label: "All Active Airports",
  },
];

export const MATCH_PRIORITY = ["iata_code", "name", "local_code", "municipality"];

export const EXCLUDED_AIRLINES = ["NK", "F9"]; // Spirit, Frontier (IATA codes)

export const CACHE_DURATION = 30 * 60 * 1000;

export const DEBOUNCE_DELAY = 300;

/** Maximum number of flight searches allowed for non-admin users */
export const MAX_FLIGHTS_NON_ADMIN = 50;

/**
 * Maximum origin × destination pairs an agent-driven estimate may request.
 * Each pair can cost a live SerpAPI call, so the cap bounds spend per request.
 */
export const MAX_AGENT_FLIGHT_PAIRS = 8;

/** Maximum agent-driven flight searches a single user may start per hour. */
export const AGENT_SEARCH_HOURLY_LIMIT = 10;

/** Trailing window over which {@link AGENT_SEARCH_HOURLY_LIMIT} is enforced. */
export const AGENT_SEARCH_WINDOW_MS = 60 * 60 * 1000;

/**
 * How long an agent estimate blocks for the inline search before returning
 * whatever priced so far with a `timeout` status. Kept under the ZED tool's
 * own 60s wait so the caller gets a real (partial) response and link rather
 * than a client-side timeout, and well under Vercel's 300s function ceiling.
 */
export const AGENT_SEARCH_DEADLINE_MS = 55 * 1000;

/** Upper bound on travelers per origin an agent estimate will accept. */
export const MAX_TRAVELERS_PER_ORIGIN = 10_000;

/** SerpAPI will not price flights beyond this horizon; reject dates past it. */
export const FLIGHT_SEARCH_MAX_HORIZON_YEARS = 1;

/** Selector matching interactive descendants whose clicks should not trigger row-level handlers. */
export const INTERACTIVE_DESCENDANT_SELECTOR = "button, a, [role='dialog']";

/** Search token for the Airbnb Reliability offsite demo. */
export const AIRBNB_RELIABILITY_DEMO_SEARCH_TOKEN = "e9859d90-35a8-4c4d-a2cb-e29c57736df5";

/**
 * Search tokens for which visa requirement indicators are enabled.
 * The visa feature is in pilot — only render badges/modals on whitelisted searches.
 */
export const VISA_ENABLED_SEARCH_TOKENS: ReadonlySet<string> = new Set([
  "b22168f3-6de1-452a-95a8-18947ee946e1",
  AIRBNB_RELIABILITY_DEMO_SEARCH_TOKEN,
]);

/** Single multiplier or a [min, max] range applied deterministically per departure. */
export type PriceMultiplierOverride = number | { min: number; max: number };

/**
 * Per-search price multipliers applied to reconstructed flight results, keyed by
 * search token and then arrival airport code. Used to dial demo prices without
 * mutating the underlying flight_search_result rows.
 *
 * A `number` applies a flat multiplier to every (departure → arrival) pair.
 * A `{ min, max }` range gives each pair its own multiplier — deterministic on
 * the departure code, so reloads stay stable.
 *
 * Multiplier is applied to `price_per_traveler`; estimates are recomputed from
 * the adjusted results so totals stay consistent.
 */
export const PER_SEARCH_PRICE_MULTIPLIERS: Record<string, Record<string, PriceMultiplierOverride>> = {
  [AIRBNB_RELIABILITY_DEMO_SEARCH_TOKEN]: {
    LAX: { min: 1.0, max: 1.5 },
  },
};

/**
 * Per-IP cap on public flight estimator searches (`startBackgroundFlightSearch` and
 * `refreshNAFlights` share one bucket). The estimator works signed out and each search fans out
 * into SerpAPI calls, so this bounds spend from one caller. Agent-driven searches are capped
 * separately per user by {@link AGENT_SEARCH_HOURLY_LIMIT}.
 */
export const PUBLIC_FLIGHT_SEARCH_RATE_LIMIT = { windowMs: 60 * 60 * 1000, maxCalls: 20 } as const;

/** Bucket key used when the request carries no client IP header, so those calls still share a cap. */
export const PUBLIC_FLIGHT_SEARCH_UNKNOWN_IP_KEY = "unknown";
